import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { FormattedDateTime } from "@/shared/datetime";
import type { MetricSortField, Period } from "@/shared/domain/metrics";
import { useInfiniteQueryMetrics } from "@/shared/domain/metrics/hooks";
import { Button } from "@/shared/foundation/primitives/Button";
import { Spinner } from "@/shared/foundation/primitives/Spinner";
import { Goto } from "@/shared/navigation/Goto";
import { nanoSecToDate, nsToIso } from "@/shared/time/time";
import type { Condition } from "@/types";

import {
  DASHBOARD_PARAMS,
  mergeParams,
  readRange,
  writeRange,
} from "../DashboardHeader/DashboardParams";

import { BucketWindow, bucketToQueryRequest } from "./bucketWindow";
import styles from "./DrillDownDialog.module.css";
import { getDrillDownTitle } from "./drillDownTitle";

export interface DrillDownDialogProps {
  open: boolean;
  onClose: () => void;
  metricName: string;
  period: Period;
  /** The clicked chart bucket, or `null` when nothing is selected. */
  bucket: BucketWindow | null;
  /** Names the window in the dialog's title, replacing the period-derived
   *  bucket label. Supplied by the RAW card, whose "bucket" is a single
   *  instant a nanosecond wide — "March 31, 2026" would describe a calendar
   *  day it never asked for. */
  windowLabel?: string;
  orgId: string | undefined;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
}

interface DrillDownSort {
  sortBy: MetricSortField;
  descending: boolean;
}

/** Session end, oldest first: the server's own default order. */
const DEFAULT_SORT: DrillDownSort = { sortBy: "time", descending: false };

/** Every column of the records table, each sortable server-side. */
const COLUMNS: {
  label: string;
  sortBy: MetricSortField;
  align?: "right";
}[] = [
  // The drill-down filters on session end (`time_filter: "end_time"`), so the
  // default "time" sort orders by exactly this column.
  { label: "Session end", sortBy: "time" },
  { label: "Published", sortBy: "published" },
  { label: "Value", sortBy: "value", align: "right" },
  { label: "Device", sortBy: "device_id" },
  { label: "Session", sortBy: "session_id" },
  { label: "Invocation", sortBy: "invocation_id" },
];

/**
 * Stands in for the active column's sort arrow while a re-sorted first page
 * loads. Hidden from assistive technology: the header cell carries
 * `aria-busy` instead, so the sort button keeps its name.
 */
function SortingSpinner({ className }: { className?: string }) {
  return <Spinner aria-hidden className={className} />;
}

/**
 * Modal that lists the raw metric records inside a single clicked window: an
 * aggregate bucket from an aggregated card, or the one-nanosecond instant
 * behind a single point on a raw card. The query is gated on `open` so
 * nothing is fetched until the user drills in.
 */
export function DrillDownDialog({
  open,
  onClose,
  metricName,
  period,
  bucket,
  windowLabel,
  orgId,
  deviceIds,
  sessionIds,
  condition,
}: DrillDownDialogProps) {
  // The sort belongs to one drilled selection: drilling into another (or
  // closing, which clears `bucket`) starts back at the default. Tagging the
  // state with the selection it was set for, rather than resetting it in an
  // effect, means a stale sort is never rendered or requested even once.
  const selectionKey = bucket
    ? [
        metricName,
        bucket.start_time,
        bucket.end_time,
        sessionIds?.join(","),
      ].join("|")
    : null;
  const [sortState, setSortState] = useState<{
    selectionKey: string | null;
    sort: DrillDownSort;
  }>({ selectionKey, sort: DEFAULT_SORT });
  const sort =
    sortState.selectionKey === selectionKey ? sortState.sort : DEFAULT_SORT;

  const onSortClick = (sortBy: MetricSortField) => {
    // A new column starts ascending; the active column flips direction.
    const next =
      sort.sortBy === sortBy
        ? { sortBy, descending: !sort.descending }
        : { sortBy, descending: false };
    setSortState({ selectionKey, sort: next });
  };

  const isDefaultSort =
    sort.sortBy === DEFAULT_SORT.sortBy &&
    sort.descending === DEFAULT_SORT.descending;
  const request =
    open && bucket
      ? bucketToQueryRequest({
          name: metricName,
          bucket,
          deviceIds,
          sessionIds,
          condition,
          // Left off in the default order, which the server applies anyway, so
          // an unsorted drill-down sends the same request it always has.
          ...(!isDefaultSort && sort),
        })
      : undefined;
  // The sort is part of the request, and so of the query key: a new sort is a
  // new query that paginates from page one, as the server requires (a page
  // token is bound to the sort that issued it).
  const {
    data,
    isLoading,
    isPlaceholderData,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQueryMetrics(request, orgId, {
    // Keep the current rows on screen while a re-sorted first page loads.
    keepPagesAcrossSortChange: true,
  });

  const title = (() => {
    if (windowLabel) {
      return windowLabel;
    } else if (bucket) {
      return getDrillDownTitle(
        // Display only — date-resolution title, so the lossy ns→number is fine.
        nsToIso(Number(bucket.start_time)),
        nsToIso(Number(bucket.end_time)),
        period,
      );
    } else {
      return "";
    }
  })();
  const records = data?.pages.flatMap((p) => p.items) ?? [];
  // Placeholder rows only ever stand in across a sort change on the same
  // selection, so while they show, the clicked column's new order is loading.
  const isSorting = isPlaceholderData;

  // Carry the dashboard's current time range + period onto the device metrics
  // tab (which renders the same MetricsDashboard and reads these params) so the
  // drill-down lands on a matching view. Period comes via prop; range lives in
  // the URL alongside it.
  const [searchParams] = useSearchParams();
  // Built through writeRange so a custom range carries its `start`/`end`
  // bounds across too — a bare `range=custom` would not survive the trip.
  const deviceViewParams = mergeParams(new URLSearchParams(), {
    ...writeRange(readRange(searchParams)),
    [DASHBOARD_PARAMS.period]: period,
  }).toString();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <div className={styles.titleRow}>
          <span>
            {metricName}
            {title ? ` — ${title}` : ""}
          </span>
          <IconButton aria-label="close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers className={styles.content}>
        {isLoading && (
          <div className={styles.state}>
            <CircularProgress size={24} />
          </div>
        )}
        {error && (
          <Alert severity="error">
            Failed to load records: {error.message}
          </Alert>
        )}
        {!isLoading && !error && records.length === 0 && (
          <div className={styles.state}>No records in this window.</div>
        )}
        {!isLoading && !error && records.length > 0 && (
          <>
            <TableContainer className={styles.tableScroll}>
              <Table size="small" stickyHeader aria-label="metric records">
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((column) => {
                      const active = sort.sortBy === column.sortBy;
                      const direction = sort.descending ? "desc" : "asc";
                      const busy = active && isSorting;
                      return (
                        <TableCell
                          key={column.sortBy}
                          align={column.align}
                          sortDirection={active ? direction : false}
                          aria-busy={busy || undefined}
                        >
                          {/* MUI's sort label used directly, like the rest of
                              this table: `@/shared/foundation` has no plain
                              table primitive to hang one on (its DataTable is
                              AG Grid, paged by page number, with no keyset
                              "Load more"), and a lone sort label would be a
                              wrapper around a table this file already takes
                              from MUI. */}
                          <TableSortLabel
                            active={active}
                            direction={active ? direction : "asc"}
                            onClick={() => onSortClick(column.sortBy)}
                            IconComponent={busy ? SortingSpinner : undefined}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((r) => (
                    // A metric has one record per session, so the session id
                    // is unique within the table.
                    <TableRow key={r.session_id}>
                      <TableCell>
                        <FormattedDateTime
                          value={
                            r.max_timestamp_ns === null
                              ? null
                              : nanoSecToDate(r.max_timestamp_ns)
                          }
                          fallback="—"
                        />
                      </TableCell>
                      <TableCell>
                        <FormattedDateTime value={r.published} />
                      </TableCell>
                      <TableCell align="right">{r.value}</TableCell>
                      <TableCell>
                        {r.device_id ? (
                          // Land on the device's Metrics tab (not the default
                          // Datasets tab) — the user is drilling into metrics.
                          // Deliberately the BARE tab (no dashboard id): the
                          // active dashboard is org-scoped view state, and
                          // carrying it across would change behavior.
                          <RobotoLinkHoverUnderline
                            to={`${Goto.createDeviceMetricsRoute(r.device_id)}?${deviceViewParams}`}
                          >
                            {r.device_id}
                          </RobotoLinkHoverUnderline>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Land on the session's Metrics tab with this metric
                            preselected, so the tree opens expanded to it and
                            its detail card is shown. The "metrics" segment and
                            "selectedMetric" param are inlined rather than
                            imported from the Sessions feature (features must not
                            import from one another). */}
                        <RobotoLinkHoverUnderline
                          to={`${Goto.route.Sessions}/${r.session_id}/metrics?selectedMetric=${encodeURIComponent(
                            metricName,
                          )}`}
                        >
                          {r.session_id}
                        </RobotoLinkHoverUnderline>
                      </TableCell>
                      <TableCell>{r.invocation_id ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Hidden while a re-sort's rows stand in for its first page:
                  their next page belongs to the old sort. */}
              {hasNextPage && !isPlaceholderData && (
                <div className={styles.loadMore}>
                  <Button
                    variant="text"
                    size="small"
                    loading={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
